import { useTranslation } from 'react-i18next'

import { DatePicker, Field, Select } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { useCases } from '@/features/cases/api/useCase'
import { useIssuingEntityOptions } from '@/features/finance/api/useFinance'
import { CounterpartySelect } from '@/features/finance/components/CounterpartySelect'
import { DocumentLines } from '@/features/finance/components/DocumentLines'
import {
  isLineComplete,
  normaliseDecimal,
  type LineDraft,
} from '@/features/finance/lines'
import {
  INVOICE_CURRENCIES,
  currencyLabel,
  // The document dates book an FX rate (FR-55), so they are seeded from the
  // invoice module's LOCAL-date helper rather than from the UTC `today()` at the
  // bottom of this file: `toISOString` answers yesterday for everyone in this
  // company between midnight and 05:00 in Ashgabat, and a rate a day early is a
  // different figure arrived at by nobody's decision.
  today as bookingToday,
  type InvoiceCurrency,
} from '@/features/finance/invoice'
import { isSuppressed } from '@/types/domain'
import type { RaiseDocumentPayload } from '@/features/finance/api/useFinance'
import type {
  DebitNote,
  DebitNoteCounterpartyKind,
  DebitNoteLinePayload,
  DebitNotePayload,
  Payment,
  PaymentMethod,
} from '@/types/domain'

/* The money editors.
 *
 * Money always arrives and leaves as a decimal STRING (AD-9) — these drafts
 * never widen an amount to `number`, and the only arithmetic here is the
 * validation check, which parses a copy and throws it away. A float that has
 * been through `Number()` and back has already lost the cent nobody will find.
 *
 * Amount fields take inputMode="decimal" so a phone offers the numeric keypad:
 * Finance enters these on the move as often as at a desk.
 */

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks. */
function isValidAmount(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** Keyed to match `finance.payments.in` / `.out` in the catalogues. */
const DIRECTIONS: Payment['direction'][] = ['in', 'out']

/** The server's own spellings, keyed to match `finance.payments.methods.*`. */
const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

/* ── Payment ──────────────────────────────────────────────────────────────── */

export interface PaymentDraft {
  /** The deal this money moves against. A payment is always on a case, so the
   *  Money page — which records across all of them — has to ask which. */
  caseId: string
  reference: string
  counterpartyName: string
  direction: Payment['direction']
  method: PaymentMethod
  /** OUR OWN legal entity: which of the company's issuing entities received or
   *  paid the money. Not the counterparty, who is named beside it. */
  issuingEntityId: string
  amount: string
  currency: string
  /** What the bank charged to move the money (migration 0058). OPTIONAL, and
   *  BLANK means no fee was recorded — never a fee of zero, which would read on
   *  a report as a transfer that cost the company nothing.
   *
   *  Two fields rather than one, because a commission is charged in the sending
   *  bank's own money as often as in the transfer's, and the server books it at
   *  its own rate. `feeCurrency` is seeded from the payment's currency and stays
   *  editable; it is only ever SENT when `feeAmount` is filled in. */
  feeAmount: string
  feeCurrency: string
  receivedOn: string
}

export function PaymentFormSheet({
  open,
  onClose,
  record,
  defaults,
  onSubmit,
  pending,
  /** What the SERVER refused, if it did. Shown over the local hint: a rejected
   *  save is the more urgent news, and the fields it names may all look valid. */
  submitError,
}: {
  open: boolean
  onClose: () => void
  record?: Payment
  /** Facts the caller already knows about a payment that does not exist yet.
   *
   *  Deliberately separate from `record`, which is `DebitNoteFormSheet`'s
   *  distinction and holds for the same reason: a partial record would seed the
   *  same fields but would also title the sheet as an edit and claim to be
   *  changing a row nobody has booked. This is a NEW payment that starts further
   *  along.
   *
   *  The values are SEEDED and then editable, never applied on submit. A form
   *  whose visible case field is overwritten behind the submit is worse than one
   *  that asks: what the reader can see and correct is what gets written. Which
   *  is also why a caller who cannot honestly answer a field — a Sales reader
   *  opening this from a direction whose haulier is withheld (AD-6) — omits it
   *  and leaves the box empty rather than seeding a blank that looks answered. */
  defaults?: Partial<PaymentDraft>
  onSubmit: (values: PaymentDraft) => void
  pending?: boolean
  submitError?: string
}) {
  const { t } = useTranslation()
  /* Both lists are read here rather than passed in, the way the quote sheet
     reads its clients: the server decides which cases and which entities this
     caller may see, so a parent passing them down would be a second, narrower
     answer to a question the endpoint already answers. */
  const { data: cases } = useCases()
  const { data: entities } = useIssuingEntityOptions()

  const [draft, setDraft, touched, markTouched] = useFormDraft<PaymentDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            caseId: record.caseId,
            reference: record.reference,
            counterpartyName: record.counterpartyName,
            direction: record.direction,
            method: record.method,
            issuingEntityId: record.issuingEntityId,
            amount: record.amount.amount,
            currency: record.amount.currency,
            // Absent stays absent. `fee` is optional on the wire and its
            // absence means "no fee was recorded" — seeding '0.00' here would
            // turn that into a claim the row never made.
            feeAmount: record.fee?.amount ?? '',
            // '' means "the payment's own", which is what the control shows and
            // what the payload resolves. A recorded fee states its own.
            feeCurrency: record.fee?.currency ?? '',
            receivedOn: record.receivedOn,
          }
        : // A new payment defaults to today: it is nearly always being entered
          // from a statement line that landed the same day. The caller's
          // prefill lands LAST, so a surface that knows the case, the direction
          // of travel or the counterparty overrides the blank rather than the
          // date.
          { ...emptyPayment, receivedOn: today(), ...defaults },
  )

  /* The counterparty this reader is not allowed to see (AD-6).
   *
   * On a payment the server serves `counterpartyName` as a bare '' when it is
   * withheld, and on this record '' has exactly ONE meaning: the field is
   * required — `PaymentInput.Validate` refuses a blank and the table's CHECK
   * refuses it again — so an empty string on a SAVED payment can only be
   * suppression, never "nobody filled it in". (`payment_dto.go` states that
   * argument where the encoding is chosen.)
   *
   * The form is then disabled and says why, which is `DebitNoteHeaderSheet`'s
   * answer to the same situation and holds for the same reason: a save from
   * here would have to re-state the counterparty, and the only names this
   * reader can pick are the ones they can see — so an enabled picker over a
   * withheld name is an invitation to overwrite a payer with somebody else.
   * A `defaults`-seeded NEW payment is a different case and needs nothing: the
   * caller omits what it cannot honestly answer and the box is simply empty,
   * which is what the `defaults` note above already prescribes. */
  const counterpartyWithheld = record !== undefined && record.counterpartyName === ''

  const caseError = touched && !draft.caseId ? t('common.form.required') : undefined
  const referenceError =
    touched && !draft.reference.trim() ? t('common.form.required') : undefined
  const counterpartyError =
    touched && !draft.counterpartyName.trim() ? t('common.form.required') : undefined
  const entityError =
    touched && !draft.issuingEntityId ? t('common.form.required') : undefined
  const amountError =
    touched && !isValidAmount(draft.amount) ? t('common.form.invalidNumber') : undefined
  /* The fee is OPTIONAL, so a BLANK box is correct and only a filled-in one is
     checked. Absence is the ordinary answer — most transfers are recorded
     without anybody knowing what the bank took — and the server agrees: it
     refuses only a half-answered pair, per field. */
  const feeTyped = draft.feeAmount.trim() !== ''
  const feeError =
    touched && feeTyped && !isValidAmount(draft.feeAmount)
      ? t('common.form.invalidNumber')
      : undefined
  /* The case and the entity are required HERE as well as server-side, where
     they come back as 422s on `caseId` and `issuingEntityId`. Neither has a
     defensible default: guessing the case would book money against the wrong
     deal, and guessing which of our entities received it would put the sum in
     the wrong bank account's netting (FR-35). */
  const invalid =
    !draft.caseId ||
    !draft.reference.trim() ||
    !draft.counterpartyName.trim() ||
    !draft.issuingEntityId ||
    !isValidAmount(draft.amount) ||
    (feeTyped && !isValidAmount(draft.feeAmount))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('finance.payments.edit') : t('finance.payments.record')}
      submitLabel={record ? undefined : t('finance.payments.record')}
      pending={pending}
      disabled={counterpartyWithheld}
      notice={counterpartyWithheld ? t('common.suppressed.reason') : undefined}
      error={submitError ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {/* The case first, because it decides what everything under it means: the
          rate basis the server books at, whose receivable moves, and which
          documents this money could ever be allocated against. */}
      <Field
        label={t('finance.payments.case')}
        required
        error={caseError}
        hint={t('finance.payments.caseHint')}
      >
        {(props) => (
          <Select
            value={draft.caseId}
            onChange={(caseId) => setDraft({ ...draft, caseId })}
            options={(cases ?? []).map((record) => ({
              value: record.id,
              label: record.number,
              hint: record.clientName,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('finance.payments.reference')}
        required
        error={referenceError}
        hint={t('finance.payments.referenceHint')}
      >
        {(props) => (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      {/* PICKED, not typed. The submitted value is still the NAME — there is no
          counterparty id on a payment and none is wanted (0035) — and what the
          picker buys is spelling: the balances read keys every counterparty by
          this string, so two spellings of one haulier are two half-right
          balances. See CounterpartySelect. */}
      <Field
        label={t('finance.balances.counterparty')}
        required
        error={counterpartyError}
        hint={t('finance.payments.counterpartyHint')}
      >
        {(props) => (
          <CounterpartySelect
            value={draft.counterpartyName}
            onChange={(counterpartyName) => setDraft({ ...draft, counterpartyName })}
            disabled={counterpartyWithheld}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field label={t('finance.payments.direction')} required className="flex-1">
          {(props) => (
            <Select
              value={draft.direction}
              onChange={(direction) => setDraft({ ...draft, direction })}
              options={toOptions(DIRECTIONS, (direction) =>
                t(`finance.payments.${direction}`),
              )}
              {...props}
            />
          )}
        </Field>

        {/* Cash or bank. It is a fact about the payment, not a preference: a
            cash receipt at a border post and a SWIFT credit reconcile against
            different evidence, and the row has to say which it was. */}
        <Field label={t('finance.payments.method')} required className="flex-1">
          {(props) => (
            <Select
              value={draft.method}
              onChange={(method) => setDraft({ ...draft, method })}
              options={toOptions(METHODS, (method) =>
                t(`finance.payments.methods.${method}`),
              )}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* OURS, not theirs. "Issuing entity" reads both ways and the two produce
          different books: this is the company entity whose account the money
          reached or left, which is what netting is computed per (FR-35). The
          counterparty is the free-text name above. */}
      <Field
        label={t('finance.payments.entity')}
        required
        error={entityError}
        hint={t('finance.payments.entityHint')}
      >
        {(props) => (
          <Select
            value={draft.issuingEntityId}
            onChange={(issuingEntityId) => setDraft({ ...draft, issuingEntityId })}
            options={(entities ?? []).map((entity) => ({
              value: entity.id,
              label: entity.name,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field
          label={t('common.fields.amount')}
          required
          error={amountError}
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field label={t('finance.payments.currency')} className="w-28 shrink-0">
          {(props) => (
            <Select
              value={draft.currency}
              onChange={(currency) => setDraft({ ...draft, currency })}
              // A currency code is already its own label.
              options={toOptions(CURRENCIES, (currency) => currency)}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* The bank's charge for moving the money — its OWN labelled area, not a
          third box crammed beside the amount and its currency.

          The separation is the point rather than the layout: the two figures
          mean different things and one of them is dangerous to confuse. What
          settles a receivable is the AMOUNT above; the fee is what the transfer
          COST the company and settles nothing (AD-11) — a 5 000 transfer with a
          25 commission settles 5 000 of debt and cost 5 025. Three amount boxes
          in one row invite exactly that addition. */}
      <div className="border-line-hair flex flex-col gap-2 border-t pt-3">
        <span className="asa-eyebrow">{t('finance.payments.feeHeading')}</span>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.payments.fee')}
            error={feeError}
            hint={t('finance.payments.feeHint')}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.feeAmount}
                onChange={(e) => setDraft({ ...draft, feeAmount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          {/* Defaults to the payment's own currency and stays editable: a
              commission is charged in the sending bank's money as often as in
              the transfer's, and the server books it at its own rate for the
              same day. An empty draft value MEANS "the payment's", so changing
              the payment's currency carries the fee along until somebody pins
              it deliberately. */}
          <Field label={t('finance.payments.feeCurrency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={draft.feeCurrency === '' ? draft.currency : draft.feeCurrency}
                onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>
      </div>

      {/* Cash cannot land in the future: a received date past today is always a
          typo, and the picker refuses it rather than booking it. */}
      <Field label={t('finance.payments.received')} required>
        {(props) => (
          <DatePicker
            value={draft.receivedOn}
            max={today()}
            onChange={(receivedOn) => setDraft({ ...draft, receivedOn })}
            {...props}
          />
        )}
      </Field>

      {/* Allocation is a separate act. Cash lands unallocated and stays visible
          as such until someone applies it — it is never auto-applied (AD-11),
          and this sheet does not offer to. */}
      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('finance.payments.allocationNote')}
      </p>
    </FormSheet>
  )
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

const emptyPayment: PaymentDraft = {
  caseId: '',
  reference: '',
  counterpartyName: '',
  direction: 'in',
  // Bank transfer rather than cash: it is the overwhelmingly common case, and
  // unlike the case and the entity a wrong default here is visible on the row
  // and costs a correction rather than a misposting.
  method: 'bank_transfer',
  issuingEntityId: '',
  amount: '',
  currency: 'USD',
  // Both blank. No fee is the ordinary answer — most transfers are recorded
  // without anybody knowing what the bank took — and a blank currency means
  // "the payment's own", which is what the control renders and the payload
  // resolves. Seeding a code here would pin the fee to USD the moment the
  // payment currency was changed to something else.
  feeAmount: '',
  feeCurrency: '',
  receivedOn: '',
}

/* ── The debit note ───────────────────────────────────────────────────────────
 *
 * ONE raise form, both screens (00-decisions §4). The case's Invoices section
 * and the Money page differ in what they PREFILL and what they FILTER, never in
 * what they ask. A second sheet would drift in wording before it drifted in
 * behaviour, and the two surfaces would then disagree about a document in front
 * of the person holding it.
 *
 * THE INVOICE BRANCH IS GONE, and it is worth a paragraph so nobody restores
 * it. This sheet used to raise either kind behind a "kind" select, and the
 * invoice half asked four things: a currency, two dates and the client's own
 * reference. Every one of them has a defensible answer without a form — the
 * currency is the rate's, the dates are today, the reference is a field on the
 * invoice — and none of them is the question somebody raising an invoice
 * actually has, which is "how much of the agreed rate is left to bill". That
 * question needs the charges, the AD-10 ceiling and the AD-9 booking in view
 * together, and a 440px column holds one of the three. So the invoice is raised
 * from `NewDocumentMenu` with no form at all and opens on its own PAGE
 * (`routes/CaseInvoicePage.tsx`); what is left here is the document that really
 * does need a body before it can exist.
 *
 * POST /cases/:id/debit-notes takes the AD-26 counterparty pair and the CHARGES
 * together, in one request. A debit note with no charges is a demand for money
 * with no stated reason, and creating it in one request is what stops that
 * moment existing at all — so the lines editor is on this form, and a note
 * without one complete line cannot be submitted. `DebitNotePayload` is the
 * deliberate opposite of the invoice's create, which takes a header and grows
 * its charges one guarded round trip at a time.
 *
 * Nothing about the recipient's printed name or address is collected. Both are
 * RESOLVED from the party the counterparty pair names (AD-26), and a body that
 * could state them could address a demand to a company nobody picked. The
 * free-text counterparty and entity boxes this sheet used to carry went with the
 * `internal/showcase` fixture that accepted them.
 */

/** One row of the AD-26 counterparty picker, as the CALLER supplies it.
 *
 *  Passed in rather than fetched here, which is the opposite of what the payment
 *  sheet does with its cases and entities, and deliberately so: who may be billed
 *  on a debit note is *this case's* client and *this case's* participants, and
 *  that list is a fact of the record the caller is already holding. A directory
 *  read from inside this sheet would offer every company in the system, which is
 *  how a demand gets addressed to a party with nothing to do with the shipment.
 *
 *  The `kind` travels ON the row rather than being asked as a second question,
 *  because the pair is only ever meaningful together: the server refuses a
 *  mismatched pair BY NAME rather than resolving the inconsistency for the
 *  client, and a picker that let the two be set from two controls is the shape
 *  that produces the mismatch. */
export interface CounterpartyChoice {
  id: string
  /** As it should read in the list. The caller composes it — this sheet has no
   *  directory to qualify a name against, and two companies with one name are
   *  told apart by the caller that knows which is which. */
  name: string
  kind: DebitNoteCounterpartyKind
}

/** The sheet's own form state — NOT a payload.
 *
 *  Shared with the header amend below, which is why it is one flat shape rather
 *  than the payload itself: the raise sends the charges and the amend does not,
 *  and one draft type that both seed keeps the two forms from growing different
 *  fields for one document. Money and every figure stay decimal STRINGS
 *  throughout (AD-9); nothing here is ever widened to a number. */
export interface DocumentDraft {
  /** The AD-26 pair, always set together from one picked row. */
  counterpartyKind: DebitNoteCounterpartyKind
  counterpartyId: string

  /** The AD-9 booking currency, frozen at creation. */
  currency: InvoiceCurrency

  /** What the note says about ITSELF — its own date. */
  documentDate: string
  /** THEIR reference for the job — a purchase order, a booking number. Printed
   *  on the paper we send them, so it is typed here rather than looked up. */
  reference: string

  /** The charges. */
  lines: LineDraft[]
}

/** Raising a NEW debit note against one case.
 *
 *  `onSubmit` hands out exactly the `RaiseDocumentPayload` that
 *  `useRaiseDocument` takes — this sheet builds the wire shape rather than a
 *  caller mapping a draft onto it. A `toDocumentPayload` living beside the
 *  mutation is what the fixture had, and it is exactly where "invoice with
 *  lines" became expressible. */
export function DebitNoteFormSheet({
  open,
  onClose,
  caseId,
  counterparties,
  defaults,
  onSubmit,
  pending,
  /** What the SERVER refused, if it did — shown over the local hint, as on the
   *  payment sheet. Our sentence and never the server's `detail`, which is
   *  English written for a log (AD-22, AD-27); callers pass `refusalMessage`. */
  submitError,
}: {
  open: boolean
  onClose: () => void
  /** The case this paper is raised against. The create is nested under it, and
   *  it is also the id the row scope is checked against. */
  caseId: string
  /** Who this case's debit notes may be addressed to (AD-26). */
  counterparties: CounterpartyChoice[]
  /** Facts the caller already knows about a document that does not exist yet —
   *  the case section seeds the charges it picked off the case's costs.
   *
   *  Deliberately separate from an existing record: a partial record would seed
   *  the same fields but would also title the sheet as an edit and make it claim
   *  to be changing a row that was never raised. This is a NEW document that
   *  starts further along, which is a different thing. */
  defaults?: Partial<DocumentDraft>
  onSubmit: (payload: RaiseDocumentPayload) => void
  pending?: boolean
  submitError?: string
}) {
  const { t } = useTranslation()

  const [draft, setDraft, touched, markTouched] = useFormDraft<DocumentDraft>(
    open ? 'new' : null,
    () => ({
      ...emptyDocument,
      documentDate: bookingToday(),
      ...defaults,
    }),
  )

  const counterpartyError =
    touched && draft.counterpartyId === '' ? t('common.form.required') : undefined

  /* The note date is NOT required: its create route defaults it, so an empty
     box is a legal request rather than a refusal waiting to happen.

     Every line must be complete, AND there must be one. A half-typed row would
     otherwise be dropped silently on submit and the charge it was going to carry
     would simply not be billed — a failure nobody notices until the recipient
     underpays; an empty editor would post a demand with no stated reason, which
     the create route refuses outright. Both are said on screen by the editor
     itself rather than only by the sheet's error line. */
  const invalid =
    draft.counterpartyId === '' ||
    draft.lines.length === 0 ||
    !draft.lines.every(isLineComplete)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.documents.new.debitNote')}
      submitLabel={t('finance.documents.raise.submit')}
      pending={pending}
      error={submitError ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(toRaisePayload(draft, caseId))
      }}
    >
      <CounterpartyField
        counterparties={counterparties}
        counterpartyKind={draft.counterpartyKind}
        counterpartyId={draft.counterpartyId}
        error={counterpartyError}
        onPick={(picked) =>
          setDraft({
            ...draft,
            counterpartyKind: picked.kind,
            counterpartyId: picked.id,
          })
        }
      />

      <CurrencyField
        value={draft.currency}
        onChange={(currency) => setDraft({ ...draft, currency })}
      />

      <DebitNoteHeaderFields
        documentDate={draft.documentDate}
        reference={draft.reference}
        onChange={(change) => setDraft({ ...draft, ...change })}
      />

      {/* The charges. Last in the sheet because it is the tallest block and the
          one that grows — the header facts stay where they were put while rows
          are being added below them. */}
      <DocumentLines
        lines={draft.lines}
        currency={draft.currency}
        showErrors={touched}
        onChange={(lines) => setDraft({ ...draft, lines })}
      />
    </FormSheet>
  )
}

/** Amending a DRAFT debit note's header — `PUT /cases/:id/debit-notes/:noteId`.
 *
 *  A second entry point over the same fields rather than a `record` prop on the
 *  sheet above, because the two acts ask different questions. The raise decides
 *  the AD-9 booking — the currency, and with it the rate and the USD equivalent —
 *  and the edit route cannot move any of it, so the currency control is ABSENT
 *  here and its absence IS the freeze. The charges are absent for the same kind
 *  of reason: `lines` are read and discarded by that route, and the three line
 *  endpoints amend a note that always has at least one. A sheet that showed
 *  either control greyed out would be describing a rule as a UI state.
 *
 *  Only a DRAFT reaches this. An issued document is not editable and the
 *  affordance is simply absent — the caller does not offer it (AC 9) — and the
 *  server refuses the write regardless, which is the guard; this is not it. */
export function DebitNoteHeaderSheet({
  open,
  onClose,
  record,
  counterparties,
  onSubmit,
  pending,
  submitError,
}: {
  open: boolean
  onClose: () => void
  record: DebitNote
  counterparties: CounterpartyChoice[]
  onSubmit: (payload: DebitNotePayload) => void
  pending?: boolean
  submitError?: string
}) {
  const { t } = useTranslation()

  /* The AD-6 group travels together: a caller without agent-identity visibility
     receives the kind, the id, the name and the address as hidden markers. That
     is not merely a blank picker — the PUT REQUIRES the pair, so a save from
     this sheet would have to re-state who owes the note, and the only parties
     this reader can pick are the ones they can see. Repointing a demand at a
     different company while its rendered paper is addressed to the first is the
     worst artifact this document produces, so the form refuses to be saved and
     says which field it cannot show, rather than offering an empty picker that
     invites the repoint. */
  const withheld =
    isSuppressed(record.counterpartyKind) || isSuppressed(record.counterpartyId)

  const [draft, setDraft, touched, markTouched] = useFormDraft<DocumentDraft>(
    open ? record.id : null,
    () => ({
      ...emptyDocument,
      counterpartyKind: isSuppressed(record.counterpartyKind)
        ? 'client'
        : record.counterpartyKind,
      counterpartyId: isSuppressed(record.counterpartyId) ? '' : record.counterpartyId,
      documentDate: record.noteDate,
      reference: record.recipientReference ?? '',
    }),
  )

  const counterpartyError =
    touched && draft.counterpartyId === '' ? t('common.form.required') : undefined
  const invalid = draft.counterpartyId === ''

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.documents.edit')}
      pending={pending}
      disabled={withheld}
      notice={withheld ? t('common.suppressed.reason') : undefined}
      error={submitError ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(toAmendPayload(draft, record))
      }}
    >
      <CounterpartyField
        counterparties={counterparties}
        counterpartyKind={draft.counterpartyKind}
        counterpartyId={draft.counterpartyId}
        error={counterpartyError}
        withheld={withheld}
        onPick={(picked) =>
          setDraft({ ...draft, counterpartyKind: picked.kind, counterpartyId: picked.id })
        }
      />

      <DebitNoteHeaderFields
        documentDate={draft.documentDate}
        reference={draft.reference}
        onChange={(change) => setDraft({ ...draft, ...change })}
      />
    </FormSheet>
  )
}

/** The AD-26 picker: ONE control over both halves of the pair.
 *
 *  The option's value carries the kind beside the id, and picking one sets both
 *  from the same row. Two controls — a kind select and a party lookup — would be
 *  the same two fields the wire carries, and would let a person leave them
 *  disagreeing; the server refuses that pair by name rather than resolving it,
 *  and guessing from the id is impossible anyway because a client id and a
 *  participant id are both uuids. */
function CounterpartyField({
  counterparties,
  counterpartyKind,
  counterpartyId,
  error,
  withheld,
  onPick,
}: {
  counterparties: CounterpartyChoice[]
  counterpartyKind: DebitNoteCounterpartyKind
  counterpartyId: string
  error?: string
  /** The reader may not see who owes this note (AD-6). The control is disabled
   *  rather than hidden: the field is on the document, and a form that silently
   *  omitted it would read as a document that has no recipient. */
  withheld?: boolean
  onPick: (choice: CounterpartyChoice) => void
}) {
  const { t } = useTranslation()

  return (
    <Field
      label={t('finance.documents.raise.counterparty')}
      required
      error={error}
      hint={t('finance.documents.raise.counterpartyHint')}
    >
      {(props) => (
        <Select
          value={counterpartyId === '' ? '' : pairValue(counterpartyKind, counterpartyId)}
          onChange={(value) => {
            const picked = counterparties.find(
              (choice) => pairValue(choice.kind, choice.id) === value,
            )
            // Only ever set from a row that exists. A pair assembled from a value
            // with no row behind it is the mismatch this control exists to
            // prevent.
            if (picked !== undefined) onPick(picked)
          }}
          options={counterparties.map((choice) => ({
            value: pairValue(choice.kind, choice.id),
            label: choice.name,
          }))}
          placeholder={t('common.form.selectPlaceholder')}
          disabled={withheld}
          searchable
          {...props}
        />
      )}
    </Field>
  )
}

/** The kind and the id as one option value. Never sent — the wire carries the
 *  two fields the server refuses independently. */
function pairValue(kind: DebitNoteCounterpartyKind, id: string): string {
  return `${kind}:${id}`
}

/** The document currency (AC-6, FR-61) — three codes.
 *
 *  The same list and the same labels the invoice side prints in, read from
 *  `invoice.ts` rather than restated: the server checks BOTH documents against
 *  `finance.PrintsIn`, so a second list here would be a second answer that only
 *  fails at the round trip. Choosing it books the rate and its date, and nothing
 *  later can move either — which is why the hint says so before the choice
 *  rather than after it. */
function CurrencyField({
  value,
  onChange,
}: {
  value: InvoiceCurrency
  onChange: (currency: InvoiceCurrency) => void
}) {
  const { t } = useTranslation()

  return (
    <Field
      label={t('finance.documents.raise.currency')}
      required
      hint={t('finance.invoices.currencyHint')}
    >
      {(props) => (
        <Select
          value={value}
          onChange={onChange}
          // The code IS the label for two of the three. The third reads RMB on
          // screen and travels as CNY, which is the one place those two
          // spellings of one currency are allowed to differ.
          options={toOptions(INVOICE_CURRENCIES, currencyLabel)}
          {...props}
        />
      )}
    </Field>
  )
}

/** The two header facts a debit note states about itself, shared by the raise
 *  and the amend so the two cannot grow different labels for one field. */
function DebitNoteHeaderFields({
  documentDate,
  reference,
  onChange,
}: {
  documentDate: string
  reference: string
  onChange: (change: Partial<DocumentDraft>) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex gap-2.5">
      <Field label={t('finance.documents.raise.noteDate')} className="flex-1">
        {(props) => (
          <DatePicker
            value={documentDate}
            onChange={(next) => onChange({ documentDate: next })}
            {...props}
          />
        )}
      </Field>

      {/* THEIR reference for the job — a purchase order, a booking number. It
          identifies a shipment rather than a company, which is why it is typed
          here and is not part of the suppressed group above. */}
      <Field label={t('finance.documents.raise.reference')} className="flex-1">
        {(props) => (
          <Input
            value={reference}
            onChange={(e) => onChange({ reference: e.target.value })}
            {...props}
          />
        )}
      </Field>
    </div>
  )
}

/** The draft as the create route takes it.
 *
 *  Built HERE rather than in a mapper beside the mutation, because the mutation
 *  takes a union and this is the only place that knows which arm was filled in.
 *  `RaiseDocumentPayload` still has an invoice arm — `useRaiseDocument` raises
 *  both kinds and the Money page's row actions read both — but no form produces
 *  one any more: a client invoice is opened by `useRaiseInvoiceDraft` with a
 *  header nobody typed.
 *
 *  Both optional dates are OMITTED when blank rather than sent as '': the server
 *  defaults the note date to today and the economic date to the note date, and
 *  an empty string on the wire is a parse failure rather than a default. */
function toRaisePayload(draft: DocumentDraft, caseId: string): RaiseDocumentPayload {
  const reference = draft.reference.trim()
  return {
    kind: 'debit-note',
    caseId,
    note: {
      counterpartyKind: draft.counterpartyKind,
      counterpartyId: draft.counterpartyId,
      currency: draft.currency,
      ...(draft.documentDate.trim() === '' ? {} : { noteDate: draft.documentDate }),
      ...(reference === '' ? {} : { recipientReference: reference }),
      lines: draft.lines.map(toDebitNoteLine),
    },
  }
}

/** The draft as the header edit takes it.
 *
 *  The currency is echoed from the RECORD rather than from the draft, and the
 *  lines are not sent at all. That route reads both and discards them — an edit
 *  form that round-trips the whole object is the ordinary client — but echoing
 *  the note's own frozen currency is the honest version of a field nothing on
 *  this form can move, where a value typed somewhere else would be a change this
 *  client believes it made and the server silently did not. */
function toAmendPayload(draft: DocumentDraft, record: DebitNote): DebitNotePayload {
  const reference = draft.reference.trim()
  return {
    counterpartyKind: draft.counterpartyKind,
    counterpartyId: draft.counterpartyId,
    currency: record.currency,
    ...(draft.documentDate.trim() === '' ? {} : { noteDate: draft.documentDate }),
    ...(reference === '' ? {} : { recipientReference: reference }),
  }
}

/** One charge as the create route takes it.
 *
 *  NO AMOUNT: it is `unitPrice × quantity`, derived once on the server. The
 *  running figure beside each row in the editor is a preview of that arithmetic
 *  and never the payload — a typed amount would be a fourth number able to
 *  disagree with the other three on the face of the document.
 *
 *  Normalised and trimmed exactly as `useWriteDebitNoteLine` does it, because
 *  these rows and the ones added later on the line routes print in one table and
 *  must arrive spelled the same way. The qualifying note is sent as '' rather
 *  than omitted: the printed row always has that line. */
function toDebitNoteLine(line: LineDraft): DebitNoteLinePayload {
  return {
    description: line.description.trim(),
    note: line.note.trim(),
    unitPrice: normaliseDecimal(line.unitPrice),
    quantity: normaliseDecimal(line.quantity),
  }
}

/** A blank note. The date is filled in by the seeding caller, which is where
 *  "today" is decided — a constant evaluated at module load would answer with
 *  the day the tab was opened. */
const emptyDocument: DocumentDraft = {
  // 'client' rather than '' so the pair is always a legal kind beside a possibly
  // empty id: the id is what the picker sets and what the required check reads,
  // and a kind that could be '' would put an unspellable value in the draft.
  counterpartyKind: 'client',
  counterpartyId: '',
  currency: 'USD',
  documentDate: '',
  reference: '',
  lines: [],
}
